import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "@/app/router";
import {
  PULL_TRIGGER_PX,
  SWIPE_EDGE_PX,
  decidePullRefresh,
  decideSwipeBack,
  historyCanGoBack,
} from "@/platform/nativeGestures";
import {
  dispatchOverlayDismiss,
  overlayIsOpen,
} from "@/platform/capacitor/androidBack";
import { platform } from "@/platform";
import { Spinner } from "@/shared/ui/core/Loading";

function editableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest("input, textarea, select, [contenteditable='true']") != null;
}

/** The scroll box under the finger, or the document when the page itself scrolls. */
function atScrollTop(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      el.scrollHeight > el.clientHeight + 1
    ) {
      return el.scrollTop <= 0;
    }
    el = el.parentElement;
  }
  return (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0;
}

/**
 * Native shell only. Left-edge swipe goes back. Pull down at the top refetches
 * the queries on screen. Neither gesture runs business actions.
 */
export function NativeShellGestures() {
  const queryClient = useQueryClient();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!platform.isNative) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let atTop = false;
    let busy = false;

    const onStart = (event: TouchEvent) => {
      if (busy || event.touches.length !== 1) {
        tracking = false;
        return;
      }
      const touch = event.touches[0];
      if (!touch || editableTarget(event.target)) {
        tracking = false;
        return;
      }
      tracking = true;
      startX = touch.clientX;
      startY = touch.clientY;
      atTop = atScrollTop(event.target);
    };

    const onMove = (event: TouchEvent) => {
      if (!tracking || event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const edgeSwipe = startX <= SWIPE_EDGE_PX && dx > 12 && dx > Math.abs(dy);
      const pulling = atTop && !overlayIsOpen() && startX > SWIPE_EDGE_PX && dy > 12 && dy > Math.abs(dx);
      if (edgeSwipe || pulling) event.preventDefault();
      setPull(pulling ? Math.min(dy, PULL_TRIGGER_PX + 24) : 0);
    };

    const onEnd = (event: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const touch = event.changedTouches[0];
      setPull(0);
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const overlayOpen = overlayIsOpen();

      const swipe = decideSwipeBack({
        startX,
        dx,
        dy,
        overlayOpen,
        canGoBack: historyCanGoBack(window.history.state),
      });
      if (swipe === "dismiss-overlay") {
        dispatchOverlayDismiss();
        return;
      }
      if (swipe === "back") {
        void router.navigate(-1);
        return;
      }

      if (
        !decidePullRefresh({
          startX,
          dx,
          dy,
          atTop,
          overlayOpen,
        })
      ) {
        return;
      }

      busy = true;
      setRefreshing(true);
      void queryClient.invalidateQueries({ type: "active" }).finally(() => {
        busy = false;
        setRefreshing(false);
      });
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [queryClient]);

  if (!platform.isNative || (pull <= 0 && !refreshing)) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[21] flex justify-center"
      style={{ transform: `translateY(${refreshing ? 12 : Math.min(pull * 0.35, 28)}px)` }}
    >
      <div className="flex size-8 items-center justify-center rounded-full bg-surface shadow-2">
        <Spinner size="sm" aria-label={refreshing ? "Đang tải lại" : "Kéo để tải lại"} />
      </div>
    </div>
  );
}
